import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useState } from "react";
import Chart from "react-apexcharts";
import "react-datepicker/dist/react-datepicker.css";

const VetConditionsChart = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState("week"); // default selected

  const data = {
    categories: [
      "Ear Infection",
      "Skin Allergies",
      "Dental Issues",
      "Vomiting",
      "Worms or parasites",
    ],
    dog: [60, 120, 80, 100, 150],
    cat: [60, 80, 120, 150, 150],
    total: 1070,
  };
  // Calculate percentages and remaining part
  const dogPercent = data.dog.map((val) =>
    ((val / data.total) * 100).toFixed(1)
  );
  const catPercent = data.cat.map((val) =>
    ((val / data.total) * 100).toFixed(1)
  );
  const remainingPercent = dogPercent.map((dogVal, i) =>
    (100 - (parseFloat(dogVal) + parseFloat(catPercent[i]))).toFixed(1)
  );

  const series = [
    {
      name: "Dog",
      data: dogPercent.map((val) => parseFloat(val)),
    },
    {
      name: "Cat",
      data: catPercent.map((val) => parseFloat(val)),
    },
    {
      name: "Remaining",
      data: remainingPercent.map((val) => parseFloat(val)),
    },
  ];

  const options = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: { show: false },
    },
    colors: ["#0052CC", "#B45AF2", "#F3F4F6"],
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 10,
        barHeight: "40%",
      },
    },

    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const index = opts.dataPointIndex;
        const total = data.dog[index] + data.cat[index];
        const percent = ((total / data.total) * 100).toFixed(1);
      },
      style: {
        colors: ["#000"],
        fontSize: "13px",
        fontWeight: 500,
      },
      offsetX: 0, // Reset offset
      textAnchor: "start",
      dropShadow: {
        enabled: false,
      },
    },

    stroke: { width: 0 },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const category = data.categories[dataPointIndex];
        const dogValue = data.dog[dataPointIndex];
        const catValue = data.cat[dataPointIndex];
        const total = dogValue + catValue;
        const percentage = ((total / data.total) * 100).toFixed(1);

        let tooltipContent =
          '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
          category +
          "</div>" +
          '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
          '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-marker" style="background-color: ' +
          w.globals.colors[0] +
          '; margin-right: 5px; display: inline-block; width: 8px; height: 8px; border-radius: 50%;"></span>' +
          '<span class="apexcharts-tooltip-text-y-label">Dog: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          dogValue +
          "</span>" +
          "</div>" +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-marker" style="background-color: ' +
          w.globals.colors[1] +
          '; margin-right: 5px; display: inline-block; width: 8px; height: 8px; border-radius: 50%;"></span>' +
          '<span class="apexcharts-tooltip-text-y-label">Cat: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          catValue +
          "</span>" +
          "</div>" +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Total: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          total +
          " (" +
          percentage +
          "%)</span>" +
          "</div>" +
          "</div>" +
          "</div>";

        return tooltipContent;
      },
    },
    xaxis: {
      categories: data.categories,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      max: 115,
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "14px",
          fontWeight: 500,
          colors: ["#000"],
        },
      },
    },
    legend: { show: false },
    grid: { show: false },
    annotations: {
      points: data.categories.map((category, index) => {
        const total = data.dog[index] + data.cat[index];
        const percent = ((total / data.total) * 100).toFixed(1);
        return {
          x: 110, // Fixed position at the right
          y: category,
          marker: {
            size: 0,
            strokeWidth: 0,
            fillOpacity: 0,
            strokeOpacity: 0,
          },
          label: {
            text: `${total} (${percent}%)`,
            offsetX: 0,
            style: {
              background: "transparent",
              color: "#000",
              fontSize: "13px",
              fontWeight: 500,
              padding: {
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
              },
            },
            borderWidth: 0,
          },
        };
      }),
    },
  };

  return (
    <div className="conditions-page">
      {/* Header */}
      <div className="header">
        <div className="date-selector flex items-center gap-2 text-gray-500 text-sm relative"></div>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, val) => val && setView(val)}
          size="small"
          sx={{
            backgroundColor: "#f0f2f5",
            borderRadius: "12px",

            // Target each button in the group
            "& .MuiToggleButtonGroup-grouped": {
              borderRadius: "7px",
              borderColor: "transparent",
              color: "#333",

              // Apply styles only when selected
              "&.Mui-selected": {
                backgroundColor: "#005EB8",
                color: "#fff",
              },
            },
          }}
        >
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="year">Year</ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* Content */}
      <div className="content">
        <h3 style={{ marginBottom: "10px" }}>Conditions</h3>

        <div className="chart-container">
          <div className="total-count">
            <span>Total:</span>
            <span>1070</span>
          </div>
          <Chart
            options={options}
            series={series}
            width="100%"
            type="bar"
            height={300}
          />
        </div>

        {/* Total Count */}
      </div>
    </div>
  );
};

export default VetConditionsChart;
